import { formatMoney } from '@/platform/lib/utils';
import { ChevronRightIcon } from '@heroicons/react/16/solid';
import {
    ArrowTrendingUpIcon,
    ClipboardDocumentCheckIcon,
    CurrencyEuroIcon,
    UsersIcon,
} from '@heroicons/react/24/outline';
import { Link, usePage } from '@inertiajs/react';
import qs from 'qs';

const integerFormatter = new Intl.NumberFormat('nl-NL');

const Skeleton = ({ className }) => (
    <span
        className={`inline-block animate-pulse rounded bg-gray-200 ${className}`}
    />
);

const StatCard = ({
    label,
    value,
    icon: Icon,
    format = integerFormatter.format,
}) => (
    <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
        <dt className='flex items-center gap-2 text-sm font-medium text-gray-500'>
            <Icon aria-hidden='true' className='size-5 text-gray-400' />
            {label}
        </dt>
        <dd className='mt-2 text-3xl font-semibold tracking-tight text-gray-900'>
            {value === undefined ? (
                <Skeleton className='h-9 w-24' />
            ) : (
                format(value)
            )}
        </dd>
    </div>
);

const BusinessRulesCard = ({ rules = [], counts, href }) => {
    const { filters } = usePage().props;

    const total = counts
        ? rules.reduce((sum, rule) => sum + (counts[rule.id] ?? 0), 0)
        : undefined;

    return (
        <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
            <div className='flex items-baseline justify-between gap-4 border-b border-gray-200 px-5 py-4'>
                <h3 className='text-sm font-medium text-gray-500'>
                    Business rules
                </h3>
                <span className='text-3xl font-semibold tracking-tight text-gray-900'>
                    {total === undefined ? (
                        <Skeleton className='h-9 w-16' />
                    ) : (
                        integerFormatter.format(total)
                    )}
                </span>
            </div>
            <ul className='divide-y divide-gray-100'>
                {rules.map(rule => {
                    const count = counts?.[rule.id];
                    const share = total ? (count / total) * 100 : 0;

                    return (
                        <li key={rule.id}>
                            <Link
                                href={`${href}?${qs.stringify({
                                    filter: rule.id,
                                    year: filters?.year || undefined,
                                    entity_id: filters?.entity_id || undefined,
                                    months: filters.months,
                                })}`}
                                className='group focus-visible:outline-primary-600 grid grid-cols-[minmax(0,12rem)_1fr_auto_auto] items-center gap-4 px-5 py-3 focus-visible:outline-2 focus-visible:-outline-offset-2'
                            >
                                <span className='group-hover:text-primary-600 truncate text-sm text-gray-700 transition-colors'>
                                    {rule.name}
                                </span>
                                <span
                                    aria-hidden='true'
                                    className='bg-primary-100 h-2 overflow-hidden rounded-full'
                                >
                                    <span
                                        className='bg-primary-600 block h-full rounded-full transition-[width] duration-500'
                                        style={{ width: `${share}%` }}
                                    />
                                </span>
                                <span className='w-10 text-right text-sm font-medium text-gray-900 tabular-nums'>
                                    {count === undefined ? (
                                        <Skeleton className='h-4 w-6' />
                                    ) : (
                                        integerFormatter.format(count)
                                    )}
                                </span>
                                <ChevronRightIcon
                                    aria-hidden='true'
                                    className='group-hover:text-primary-600 size-4 text-gray-300 transition-colors'
                                />
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const Statistics = () => {
    const {
        auth,
        employees_count,
        high_premiums,
        business_rules,
        contract_checks,
        actual_savings,
        business_rules_enum,
    } = usePage().props;

    const baseUrl = `/customers/${auth?.user?.selected_customer_id}/services/ww-check`;

    return (
        <div className='grid grid-cols-1 gap-4 xl:grid-cols-3'>
            <dl className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:col-span-2'>
                <StatCard
                    label='Werknemers'
                    value={employees_count}
                    icon={UsersIcon}
                />
                <StatCard
                    label='Hoge premies'
                    value={high_premiums}
                    icon={ArrowTrendingUpIcon}
                />
                <StatCard
                    label='Contract check'
                    value={contract_checks}
                    icon={ClipboardDocumentCheckIcon}
                />
                <StatCard
                    label='Besparingen'
                    value={actual_savings}
                    icon={CurrencyEuroIcon}
                    format={formatMoney}
                />
            </dl>
            <BusinessRulesCard
                rules={business_rules_enum}
                counts={business_rules}
                href={`${baseUrl}/business-rules`}
            />
        </div>
    );
};

export default Statistics;
